import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCreateProgram } from "../../api/hooks";
import { ErrorState } from "../../components/states";
import { Button } from "../../components/ui/button";
import { Field, Textarea } from "../../components/ui/fields";

export function NewProgram() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateProgram();
  const schema = z.object({ request: z.string().trim().min(10, t("create.tooShort")).max(4000, t("create.tooLong")) });
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { request: "" } });
  const examples = t("create.examples", { returnObjects: true }) as string[];

  const submit = form.handleSubmit(async ({ request }) => {
    const { programId } = await create.mutateAsync(request);
    await navigate({ to: "/programs/$programId", params: { programId } });
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <h1 className="text-xl font-semibold">{t("create.heading")}</h1>
      <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
        <Field label={t("create.label")} htmlFor="request" hint={t("create.help")} error={form.formState.errors.request?.message}>
          <Textarea id="request" rows={6} {...form.register("request")} />
        </Field>
        {create.isError ? <ErrorState title={t("programs.loadFailed")} error={create.error} /> : null}
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? t("create.submitting") : t("create.submit")}
        </Button>
      </form>
      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t("create.examplesHeading")}</h2>
        <ul className="space-y-2">
          {examples.map((example) => (
            <li key={example}>
              <button
                type="button"
                className="w-full rounded-md border border-border p-3 text-left text-sm hover:bg-accent"
                onClick={() => form.setValue("request", example, { shouldValidate: true })}
              >
                {example}
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
